import { colors, radius, spacing } from "@/components/design-tokens";
import { DISCLAIMER_TEXT } from "@/constants/disclaimer";
import { CHAT_PLACEHOLDER_TEXT, MAX_MESSAGE_LENGTH } from "@/constants/search-constants";
import { useWidgetRuntimeConfig, WidgetRuntimeConfigProvider } from "@/context/widget-runtime-config";
import { env } from "@/env";
import { useLimitedInput } from "@/hooks/use-limited-input";
import { CHAT_MODE, useChatState } from "@/store/chat-state";
import { useModalState } from "@/store/modal-state";
import { resolveSuggestedQuestions } from "@/utils/resolve-suggested-questions";
import { resolvePlaceholder } from "@/utils/resolve-placeholder";
import {
  applyThemeVariables,
  applyThemeWithOverrides,
  buildAttributeOverrides,
  fetchWidgetTheme,
  injectBaseStyles,
  readWidgetAttributes,
} from "@/utils/widget-runtime";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import styled from "@emotion/styled";
import type { FC } from "react";
import { useRef } from "react";
import ReactDOM from "react-dom/client";
import { Sentry } from "../../instrument";
import { DisclaimerRow } from "../chat-components/disclaimer-row";
import { SuggestionCards } from "../chat-components/suggestion-cards";
import { QueryInput } from "../primitives/query-input";
import { WidgetHeader } from "../primitives/widget-header";

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing["2xl"]}px;
  padding: ${spacing["3xl"]}px;
  background-color: ${colors.surface};
  border-radius: ${radius.xl}px;
  width: 100%;
  max-width: 400px;
  min-width: 250px;
  box-sizing: border-box;
`;

type ChatTriggerProps = {
  showSummarise?: boolean;
};

const ChatTrigger: FC<ChatTriggerProps> = ({ showSummarise = false }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const {
    showChatQuestions,
    widgetPlaceholderText,
    widgetSuggestedQuestions,
  } = useWidgetRuntimeConfig();
  const { value, isValid, error, setValue, reset } = useLimitedInput("", {
    maxLength: MAX_MESSAGE_LENGTH,
    showError: true,
  });
  const { submitQuery, setContextUrl, setUserQuery, setMode } = useChatState();
  const { openModal } = useModalState();

  const handleSubmit = (content: string) => {
    if (!isValid) {
      return;
    }
    // Clear contextUrl when asking a regular question
    setContextUrl(undefined);
    submitQuery(content);
    openModal();
    reset();
  };

  const handleSummariseThisPage = () => {
    const currentUrl = window.location.href;
    if (currentUrl) {
      // Set the context URL for summarization
      setContextUrl(currentUrl);
      // Use a clean title for the UI
      setUserQuery("Page Summary");
      // Set mode to INPUT to trigger the chat flow
      setMode(CHAT_MODE.INPUT);
      // Open modal to show the result
      openModal();
    }
  };

  const handleSuggestionClick = (query: string) => {
    // Check if this is the special "Summarise this page" suggestion
    if (query === "SUMMARISE_THIS_PAGE") {
      handleSummariseThisPage();
    }
    else {
      handleSubmit(query);
    }
  };

  return (
    <ChatContainer ref={containerRef}>
      <WidgetHeader title="How can we help you today?" />
      <QueryInput
        value={value}
        onChange={setValue}
        onSubmit={handleSubmit}
        placeholder={widgetPlaceholderText}
        error={error}
      />
      <SuggestionCards
        suggestions={[
          // Add "Summarise this page" as the first suggestion only if showSummarise is true
          ...(showSummarise
            ? [
                {
                  question: "SUMMARISE_THIS_PAGE",
                  displayText: "Summarise this page",
                },
              ]
            : []),
          // Then add the regular suggestions when enabled for the tenant.
          ...(showChatQuestions
            ? widgetSuggestedQuestions?.map(question => ({ question })) ?? []
            : []),
        ]}
        onSuggestionClick={handleSuggestionClick}
        isColumnLayout
      />
      <DisclaimerRow />
    </ChatContainer>
  );
};

class ChatTriggerWebComponent extends HTMLElement {
  private root: ReactDOM.Root | null = null;
  private isMounted = false;
  private abortController: AbortController | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    this.isMounted = true;
    this.abortController?.abort();
    this.abortController = new AbortController();
    // Inject base styles including system fonts
    injectBaseStyles(this);

    const attributes = readWidgetAttributes(this);
    if (!attributes.apiKey) {
      console.error("Tenant UI Widget: api-key attribute is required.");
      return;
    }

    const apiUrl = attributes.apiUrl ?? env.API_URL;
    const runtimeConfig = {
      apiKey: attributes.apiKey,
      apiUrl,
      organizationSlug: undefined as string | undefined,
      widgetDisclaimerText: DISCLAIMER_TEXT,
      widgetSuggestedQuestions: resolveSuggestedQuestions(
        attributes.suggestedQuestions,
        undefined,
      ),
      showChatQuestions: true,
      widgetPlaceholderText: resolvePlaceholder(attributes.placeholderText, undefined, CHAT_PLACEHOLDER_TEXT),
      langfuse: null as { baseUrl: string; publicKey: string; environment?: string } | null,
    };

    const colorOverrides = buildAttributeOverrides(attributes);

    applyThemeVariables(this, colorOverrides, true);

    // Read the show-summarise attribute
    const showSummarise = this.hasAttribute("show-summarise");

    const cache = createCache({
      key: "shadow-emotion",
      container: this.shadowRoot as unknown as Element,
    });

    void fetchWidgetTheme(apiUrl, attributes.apiKey, this.abortController.signal).then(({ theme, disclaimerText, suggestedQuestions, showChatQuestions, placeholderText, organizationSlug, langfuse }) => {
      if (!this.isMounted) {
        return;
      }
      applyThemeWithOverrides(this, theme, colorOverrides);
      runtimeConfig.organizationSlug = organizationSlug ?? runtimeConfig.organizationSlug;
      runtimeConfig.widgetDisclaimerText = disclaimerText ?? runtimeConfig.widgetDisclaimerText;
      runtimeConfig.widgetSuggestedQuestions = resolveSuggestedQuestions(
        attributes.suggestedQuestions,
        suggestedQuestions,
      );
      runtimeConfig.showChatQuestions = showChatQuestions;
      runtimeConfig.widgetPlaceholderText = resolvePlaceholder(
        attributes.placeholderText,
        placeholderText,
        CHAT_PLACEHOLDER_TEXT,
      );
      runtimeConfig.langfuse = langfuse ?? null;
      if (this.root) {
        this.root.render(
          <CacheProvider value={cache}>
            <WidgetRuntimeConfigProvider value={runtimeConfig}>
              <Sentry.ErrorBoundary
                beforeCapture={(scope) => {
                  scope.setTag("component", "tenant-ai-widget-chat-trigger");
                  scope.setContext("widget", {
                    shadowDOM: true,
                    component: "ChatTriggerWebComponent",
                  });
                }}
              >
                <ChatTrigger showSummarise={showSummarise} />
              </Sentry.ErrorBoundary>
            </WidgetRuntimeConfigProvider>
          </CacheProvider>,
        );
      }
    });

    this.root = ReactDOM.createRoot(this.shadowRoot as ShadowRoot);
    this.root.render(
      <CacheProvider value={cache}>
        <WidgetRuntimeConfigProvider value={runtimeConfig}>
          <Sentry.ErrorBoundary
            beforeCapture={(scope) => {
              scope.setTag("component", "tenant-ai-widget-chat-trigger");
              scope.setContext("widget", {
                shadowDOM: true,
                component: "ChatTriggerWebComponent",
              });
            }}
          >
            <ChatTrigger showSummarise={showSummarise} />
          </Sentry.ErrorBoundary>
        </WidgetRuntimeConfigProvider>
      </CacheProvider>,
    );
  }

  disconnectedCallback() {
    this.isMounted = false;
    this.abortController?.abort();
    this.abortController = null;
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }
}

export default ChatTriggerWebComponent;
